import { useState, useRef } from "react";
import { Box, Typography, Avatar, Button, Paper, Chip } from "@mui/material";
import { useTheme } from "@mui/material";
import { CustomDialogHandle } from "../components/CustomDialog";
import AddIcon from "@mui/icons-material/Add";
import StorefrontRounded from "@mui/icons-material/StorefrontRounded";
import StorefrontOutlined from "@mui/icons-material/StorefrontOutlined";
import {
  CustomDialog,
  AddingRateBox,
  MerchantCard,
  EmptyState,
  Loading,
  Error,
  PageHeader,
} from "../components";
import { TForm } from "../utils/types";
import { useMerchants } from "../hooks/useMerchants";
import { useMethods } from "../hooks/useMethods";
import { useMedia } from "../context/MediaContext";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import AddingMerchant from "../components/AddingMerchant";
const Merchants: React.FC = () => {
  const dialogRef = useRef<CustomDialogHandle>(null);
  const theme = useTheme();
  const { currency, methods } = useMethods();
  const { isTablet } = useMedia();
  const { merchants, rates, loading, error, fetchData } = useMerchants();
  const containerRef = useRef<HTMLDivElement>(null);
  const addMerchantDialogRef = useRef<CustomDialogHandle>(null);
  const [display, setDisplay] = useState<string>("");

  const [form, setForm] = useState<TForm>({
    operation_type: null,
    currency: null,
    rate: null,
    payment_method: null,
  });

  const [chosenMerchant, setChosenMerchant] = useState<{
    name: string;
    id: string;
  }>({
    name: "",
    id: "",
  });

  if (loading) {
    return <Loading variant="cards" />;
  }
  if (error) {
    return <Error error={error} />;
  }
  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          paddingTop: { xs: "16px", sm: "24px" },
          maxHeight:
            chosenMerchant.name !== ""
              ? {
                  xs: "calc(var(--content-max-height) + 22dvh)",
                  md: "90vh",
                }
              : {
                  xs: "calc(var(--content-max-height) + 22dvh)",
                  md: "90vh",
                },
          overflowY: "auto",
          "&::-webkit-scrollbar": { display: "none" },
          overflowX: "hidden",
        }}
      >
        <Box sx={{ width: "95%", mx: "auto" }}>
          <PageHeader
            title="Мерчанты"
            description="Настройки магазинов, ставок и платёжных направлений"
            icon={StorefrontRounded}
            actions={
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => addMerchantDialogRef.current?.openDialog()}
              >
                Добавить мерчанта
              </Button>
            }
          />
        </Box>
        {merchants.length === 0 ? (
          <Box sx={{ width: "95%", mx: "auto" }}>
            <EmptyState
              icon={StorefrontOutlined}
              title="Мерчантов пока нет"
              description="Создайте первого мерчанта, чтобы настроить его ставки и направления."
              action={
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => addMerchantDialogRef.current?.openDialog()}
                >
                  Создать мерчанта
                </Button>
              }
            />
          </Box>
        ) : (
        <Box
          sx={{
            display: "flex",
            width: "95%",
            margin: "0 auto",
            marginTop: "15px",
            alignItems: "flex-start",
            gap: "24px",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                chosenMerchant.name !== ""
                  ? "1fr"
                  : {
                      xs: "1fr",
                      sm: "repeat(auto-fill, minmax(260px, 1fr))",
                    },
              gap: "12px",
              paddingBottom: "20px",

              width:
                isTablet && chosenMerchant.name !== ""
                  ? 0
                  : chosenMerchant.name !== ""
                    ? "36%"
                    : "100%",

              minWidth: 0,
              flexShrink: 0,
              overflow: "hidden",

              transition: "width 0.4s ease-in-out",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            {merchants.map((el: any) => {
              const isSelected = String(el.id) === chosenMerchant.id;
              const ratesCount = rates[el.id]?.length ?? 0;

              return (
              <Paper
                key={el.id}
                onClick={() => {
                  setChosenMerchant({
                    name: el.name,
                    id: String(el.id),
                  });
                  containerRef.current?.scrollTo({
                    top: 0,
                    behavior: "smooth",
                  });
                }}
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  p: "14px",
                  minHeight: "82px",
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : "divider",
                  backgroundColor: isSelected
                    ? theme.palette.mode === "dark"
                      ? "rgba(124, 116, 255, 0.12)"
                      : "rgba(99, 91, 255, 0.06)"
                    : "background.paper",
                  boxShadow: "none",
                  cursor: "pointer",
                  transition:
                    "transform 0.2s ease, border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    borderColor: "primary.light",
                    boxShadow:
                      theme.palette.mode === "dark"
                        ? "0 12px 28px rgba(0,0,0,.18)"
                        : "0 12px 28px rgba(31,42,68,.09)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      flexShrink: 0,
                      background:
                        "linear-gradient(145deg, #7C74FF 0%, #5148D8 100%)",
                      textAlign: "center",
                      color: "white",
                      boxShadow: "0 8px 18px rgba(81,72,216,.24)",
                    }}
                  >
                    <Typography
                      sx={{
                        textTransform: "uppercase",
                        
                        fontSize: "14px",
                        fontWeight: 700,
                      }}
                    >
                      {el.name?.slice(0, 2)}
                    </Typography>
                  </Avatar>
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        lineHeight: 1.25,
                        fontWeight: 700,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {el.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        mt: "7px",
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        label={`ID ${el.id}`}
                        size="small"
                        sx={{ height: 22, fontSize: "11px" }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {ratesCount} {ratesCount === 1 ? "ставка" : "ставок"}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "10px",
                      display: "grid",
                      placeItems: "center",
                      color: isSelected ? "primary.main" : "text.secondary",
                      backgroundColor: isSelected
                        ? "rgba(99, 91, 255, 0.12)"
                        : "action.hover",
                      flexShrink: 0,
                    }}
                  >
                    <ChevronRightRounded fontSize="small" />
                  </Box>
                </Box>
              </Paper>
              );
            })}
          </Box>
          <Box
            sx={{
              width:
                isTablet && chosenMerchant.name !== ""
                  ? "100%"
                  : chosenMerchant.name !== ""
                    ? "calc(64% - 24px)"
                    : 0,

              transition: "width 0.4s ease-in-out",
            }}
          >
            {chosenMerchant.name !== "" ? (
              <Box>
                {isTablet && (
                  <Button
                    onClick={() => setChosenMerchant({ name: "", id: "" })}
                    variant="outlined"
                    startIcon={<ArrowBackIosNewIcon />}
                    sx={{
                      width: "100%",
                      textTransform: "none",
                      borderRadius: "12px",
                      py: 1.2,
                      fontSize: "14px",
                      "&:hover": {
                        borderColor: "primary.main",
                        backgroundColor: "rgba(99, 91, 255, 0.08)",
                      },
                    }}
                  >
                    Назад
                  </Button>
                )}
                <MerchantCard
                  display={display}
                  setDisplay={setDisplay}
                  fetchData={fetchData}
                  dialogRef={dialogRef}
                  rates={rates}
                  merchantName={chosenMerchant?.name}
                  merchantID={chosenMerchant?.id}
                />
              </Box>
            ) : null}
          </Box>
        </Box>
        )}
      </Box>
      <CustomDialog
        ref={dialogRef}
        dialog={{
          title: "Введите данные",
          sx: {
            display: "flex",
            flexDirection: "column",
            padding: "25px",
          },
          context: null,
        }}
      >
        <AddingRateBox
          form={form}
          currency={currency}
          methods={methods}
          setForm={setForm}
          merchantId={chosenMerchant.id}
          type={"merchant"}
          dialogRef={dialogRef}
          fetchData={fetchData}
        />
      </CustomDialog>
      <CustomDialog
        ref={addMerchantDialogRef}
        dialog={{
          title: "",
          sx: {
            display: "flex",
            flexDirection: "column",
          },
          context: null,
        }}
      >
        <AddingMerchant />
      </CustomDialog>
    </>
  );
};

export default Merchants;
